import { Link, useLocation } from 'react-router-dom'
import { XStack, YStack, Text } from 'tamagui'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { EventsIcon } from '../assets/icons/EventsIcon'
import { MarketplaceIcon } from '../assets/icons/MarketplaceIcon'
import { PlacesIcon } from '../assets/icons/PlacesIcon'
import { PerksIcon } from '../assets/icons/PerksIcon'
import { GroupsIcon } from '../assets/icons/GroupsIcon'
import { ChatIcon } from '../assets/icons/ChatIcon'
import { ProfileIcon } from '../assets/icons/ProfileIcon'
import { BOTTOM_NAV_HEIGHT } from '../constants/layout'

type Tab = { to: string; label: string }

// Keyed by route, not label — labels are localized and would miss the lookup
// in any non-English language.
const iconMap: Record<string, React.ComponentType<any>> = {
  '/chat': ChatIcon,
  '/events': EventsIcon,
  '/groups': GroupsIcon,
  '/marketplace': MarketplaceIcon,
  '/places': PlacesIcon,
  '/perks': PerksIcon,
  '/profile': ProfileIcon,
}

export default function TopTabs({ tabs, renderRight }: { tabs: Tab[]; renderRight?: () => React.ReactNode }) {
  const { pathname } = useLocation()
  const [isSmallScreen, setIsSmallScreen] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 420)
    }
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  return (
    <YStack
      elevation={3}
      zi={100}
      w="100%"
      bc="$background"
      borderTopWidth={1}
      borderColor="#e5e7eb"
      minHeight={BOTTOM_NAV_HEIGHT}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)',
        backgroundColor: '#ffffff',
      }}
    >
      <XStack
        px={isSmallScreen ? "$2" : "$4"}
        py={isSmallScreen ? "$1" : "$2"}
        ai="center"
        jc="space-between"
      >
        <XStack 
          gap={isSmallScreen ? 0 : "$4"} 
          ai="center"
          jc="space-around"
          f={1}
        >
          {tabs.map((t) => {
            const active = pathname.startsWith(t.to)
            const IconComponent = iconMap[t.to]
            const iconColor = active ? '#000000' : '#9ca3af'

            return (
              <YStack key={t.to} pos="relative" ai="center" gap="$1">
                <Link to={t.to} style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {IconComponent ? (
                    <IconComponent isActive={active} color={iconColor} />
                  ) : null}
                  <Text
                    color={active ? '#000000' : '#9ca3af'}
                    fow={active ? '700' : '500'}
                    fontSize={isSmallScreen ? "$1" : "$2"}
                  >
                    {t.label}
                  </Text>
                </Link>
                {active && (
                  <motion.div layoutId="tab-underline" style={{ height: 2, background: '#000000', width: '100%', marginTop: 4 }} />
                )}
              </YStack>
            )
          })}
        </XStack>
        {renderRight ? renderRight() : null}
      </XStack>
    </YStack>
  )
}


